import type { FC, PropsWithChildren } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAppSelector } from "store/hooks";
import { selectIsSoundMuted } from "store/slices/user";
import { ClassroomAmbienceContext } from "./context";
import { useBackgroundSound } from "./useBackgroundSound";

interface Props extends PropsWithChildren {
  isEnabled?: boolean;
  /**
   * AIDEV-NOTE: Zadaná hodnota (i `false`) přebíjí centrální volbu studenta. Jediný volající
   * je `BookExam` — předává snímek `session.settings.hasAmbience`, omezený jen na krok
   * potítka (`step === "preparation"`); ne aktuální centrální volbu a ne `useExamSettings`,
   * ten jen předvyplňuje lightbox nastavení zkoušky a při potvrzení zapisuje centrální volbu
   * (R-3), providerem sám netočí. Efekt níž reaguje jen na změnu vyřešené hodnoty `withSound`,
   * takže v tomto režimu tlačítko přehrávání zvuk neřídí — provider proto NENÍ určený ke
   * kombinaci s `ClassroomAmbienceButton`/přepínačem v PauseExercise (a zkouška takové
   * tlačítko úmyslně nemá). [TD-368]
   */
  withSound?: boolean;
}

const ClassroomAmbienceProvider: FC<Props> = ({ children, isEnabled = true, withSound: withSoundProp }) => {
  const { withSound: centralWithSound, setWithSound } = useBackgroundSound();
  const withSound = withSoundProp ?? centralWithSound;
  const isSoundMuted = useAppSelector(selectIsSoundMuted);
  const [isSoundOn, setIsSoundOn] = useState<boolean>(false);

  const ref = useRef<HTMLAudioElement | null>(null);

  const pause = useCallback(() => {
    if (ref.current === null) return;
    ref.current.pause();
    setIsSoundOn(false);
  }, [ref]);

  const play = useCallback(() => {
    if (ref.current === null) return;
    ref.current.play().catch(console.error);
    setIsSoundOn(true);
  }, [ref]);

  const handleToggleSound = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Safari requires a user interaction (like a click or tap) before it will play audio
      // https://webkit.org/blog/7734/auto-play-policy-changes-for-macos/

      event.stopPropagation();

      if (!isEnabled) return;

      if (ref.current === null) return;

      const next = !isSoundOn;

      if (next) {
        ref.current.volume = 1;
        ref.current.play().catch(console.error);
      }

      // AIDEV-NOTE: zápis do centrální volby — přehrání/pauzu a isSoundOn dotáhne effect níž.
      setWithSound(next);
    },
    [isEnabled, isSoundOn, ref, setWithSound],
  );

  const startDelayedAudio = useCallback(() => {
    document.removeEventListener("click", startDelayedAudio);

    if (!isEnabled || !withSound || !ref.current?.paused) return;

    ref.current.play().catch(console.error);
  }, [isEnabled, withSound, ref]);

  useEffect(() => {
    document.addEventListener("click", startDelayedAudio);

    return () => {
      document.removeEventListener("click", startDelayedAudio);
    };
  }, [startDelayedAudio]);

  useEffect(() => {
    if (!isEnabled) {
      pause();
      return;
    }

    if (withSound) play();
    else pause();
  }, [isEnabled, pause, play, withSound]);

  useEffect(() => {
    if (ref.current === null) return;
    ref.current.muted = isSoundMuted;
  }, [isSoundMuted]);

  return (
    <ClassroomAmbienceContext.Provider value={{ isSoundOn, onToggleSound: handleToggleSound }}>
      {children}

      <audio ref={ref} style={{ display: "none" }} loop>
        <source src="/audio/sum_tridy.mp4" type="audio/mp4" />
      </audio>
    </ClassroomAmbienceContext.Provider>
  );
};

export default ClassroomAmbienceProvider;
